import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { TrunkBaseConfigurationService } from '@core/services/trunk-base-configuration/trunk-base-configuration.service';
import { SortingHelperService } from '@shared/helpers/utils/sorting-helper.service';
import { SoTableComponent } from '../so-table.component';

type TrackByInput = ((index: number, item: Record<string, any>) => any) | string | null | undefined;
type TrackByFunction = (index: number, item: Record<string, any>) => any;

@Component({
  selector: 'app-so-table-virtual-scroll',
  templateUrl: './so-table-virtual-scroll.component.html',
  styleUrls: [
    '../so-table.component.scss',
    './so-table-virtual-scroll.component.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SoTableVirtualComponent extends SoTableComponent implements AfterViewInit {
  @Input() trackBy: TrackByInput = null;
  @Input() virtualItemSize = 48;
  @Input() virtualMinBufferPx = 240;
  @Input() virtualMaxBufferPx = 720;

  @ViewChild(CdkVirtualScrollViewport) private viewport?: CdkVirtualScrollViewport;

  private viewportReady = false;
  private pendingViewportUpdate = false;
  private resolvedTrackBy: TrackByFunction = (index: number) => index;

  constructor(
    sortingHelperService: SortingHelperService,
    trunkBaseConfigurationService: TrunkBaseConfigurationService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    super(sortingHelperService, trunkBaseConfigurationService);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.updateTrackByResolver();
  }

  override ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);

    if (changes['trackBy']) {
      this.updateTrackByResolver();
    }

    if (changes['tableData']) {
      this.refreshViewport();
    }
  }

  ngAfterViewInit(): void {
    this.viewportReady = true;
    this.viewport?.checkViewportSize();

    if (this.pendingViewportUpdate) {
      this.pendingViewportUpdate = false;
      this.viewport?.checkViewportSize();
    }
  }

  override handleTableDataChange(previousTableDataLenght: number): void {
    super.handleTableDataChange(previousTableDataLenght);
    this.refreshViewport();
  }

  override handleSearch(value: string): void {
    super.handleSearch(value);
    this.refreshViewport();
  }

  override updateDisplayedData(): void {
    super.updateDisplayedData();
    this.refreshViewport();
  }

  get displayedRows(): Record<string, any>[] {
    return this.enablePagination ? this.filteredData : this.tableData;
  }

  readonly trackByRow = (index: number, item: Record<string, any>): any =>
    this.resolvedTrackBy(index, item);

  protected override cloneData(value: Record<string, any>[]): Record<string, any>[] {
    if (!value) {
      return [];
    }

    const globalStructuredClone = (globalThis as { structuredClone?: <T>(input: T) => T }).structuredClone;

    if (typeof globalStructuredClone === 'function') {
      return globalStructuredClone(value);
    }

    return super.cloneData(value);
  }

  protected override markForCheck(): void {
    this.cdr.markForCheck();
  }

  protected refreshViewport(): void {
    if (this.viewportReady) {
      this.viewport?.checkViewportSize();
    } else {
      this.pendingViewportUpdate = true;
    }
  }

  private updateTrackByResolver(): void {
    const trackBy = this.trackBy;

    if (typeof trackBy === 'function') {
      this.resolvedTrackBy = trackBy;
    } else if (typeof trackBy === 'string' && trackBy.trim().length > 0) {
      this.resolvedTrackBy = (_index, item) => this.getPropertyValue(item, trackBy);
    } else {
      this.resolvedTrackBy = (index) => index;
    }

    this.markForCheck();
  }
}